import { Component, OnDestroy, OnInit } from '@angular/core';
import { TeamsService } from '../../services/teams/teams.service';
import { SpinnerService } from '../../services/spinner/spinner.service';
import { StripeService } from '../../services/stripe/stripe.service';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { ChargeHistoryQuery, Invoice } from '../../interfaces/stripe/invoice.interface';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-stripe-chargehistory',
    templateUrl: './stripe-chargehistory.component.html',
    styleUrls: ['./stripe-chargehistory.component.scss'],
})
export class StripeChargehistoryComponent implements OnInit, OnDestroy {
    public charges: any;
    public invoices: Invoice[] = [];
    public invoicesWithoutPaymentMethod: Invoice[] = [];
    public items: any;
    public form: any;
    public isLoading = false;
    public loadError = '';
    public dateError = '';
    public pageIndex = 0;
    public hasMore = false;
    public hasLoadedPage = false;
    public readonly displayTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    private selectedPaymentMethod = '';
    private pageCursors: (string | undefined)[] = [undefined];
    private nextCursor: string | null = null;
    private dateQuery: ChargeHistoryQuery = {};
    private selectedTeamId = '';
    private paymentMethodsRequestVersion = 0;
    private invoicesRequestVersion = 0;
    private formSubscription?: Subscription;
    private wellspaceSubscription?: Subscription;

    constructor(
        private teamsService: TeamsService,
        private spinnerService: SpinnerService,
        private stripeService: StripeService,
        private formBuilder: UntypedFormBuilder
    ) {}

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            method: new UntypedFormControl(),
            startDate: new UntypedFormControl(''),
            endDate: new UntypedFormControl(''),
        });
        this.formSubscription = this.form.valueChanges.subscribe((): void => {
            const value = this.form.get('method').value;
            if (value && this.selectedTeamId) {
                this.loadCharges(this.selectedTeamId, value?.stripePaymentMethod);
            }
        });
        this.wellspaceSubscription = this.teamsService.wellspaceChangedObservable.subscribe((wellspace: any) => {
            const teamId = `${wellspace?.id || ''}`.trim();
            this.resetPresentationState();
            if (!teamId) {
                return;
            }

            this.selectedTeamId = teamId;
            const requestVersion = ++this.paymentMethodsRequestVersion;
            this.isLoading = true;
            this.teamsService.getPaymentMethods(teamId).then((result: any[]): void => {
                if (
                    requestVersion !== this.paymentMethodsRequestVersion
                    || this.selectedTeamId !== teamId
                ) {
                    return;
                }
                this.items = result.filter((paymentMethod: any) => paymentMethod && paymentMethod.stripePaymentMethod);
                if (this.items.length === 1) {
                    this.form.patchValue({ method: this.items[0] }, { emitEvent: false });
                    this.loadCharges(teamId, this.items[0].stripePaymentMethod);
                } else {
                    this.selectDefaultPaymentMethod(wellspace);
                }
                if (this.items.length === 0) {
                    this.isLoading = false;
                }
            }).catch((error: any): void => {
                if (
                    requestVersion !== this.paymentMethodsRequestVersion
                    || this.selectedTeamId !== teamId
                ) {
                    return;
                }
                this.items = [];
                this.isLoading = false;
                this.loadError = 'Unable to load payment methods for this Team.';
                console.error('Error loading Team payment methods', error);
            });
        });
    }

    ngOnDestroy(): void {
        this.paymentMethodsRequestVersion++;
        this.invoicesRequestVersion++;
        this.formSubscription?.unsubscribe();
        this.wellspaceSubscription?.unsubscribe();
        this.spinnerService.hide();
    }

    selectDefaultPaymentMethod(wellspace: any): any {
        if (!wellspace && this.items.length <= 0) {
            this.isLoading = false;
            return (this.charges = []);
        }
        const defaultPaymentMethod = wellspace?.defaultPaymentMethod;
        for (const item of this.items) {
            if (item?.stripePaymentMethod === defaultPaymentMethod && item?.stripeCustomerId) {
                this.form.patchValue({ method: item }, { emitEvent: false });
                this.loadCharges(wellspace.id, item?.stripePaymentMethod);
                return;
            }
        }
        this.charges = [];
        this.isLoading = false;
    }

    loadCharges(teamId: string, paymentMethod: string): void {
        const normalizedTeamId = `${teamId || ''}`.trim();
        const normalizedPaymentMethod = `${paymentMethod || ''}`.trim();
        if (
            !normalizedTeamId
            || !normalizedPaymentMethod
            || this.selectedTeamId !== normalizedTeamId
        ) {
            return;
        }
        this.selectedPaymentMethod = normalizedPaymentMethod;
        this.pageIndex = 0;
        this.pageCursors = [undefined];
        this.nextCursor = null;
        this.hasMore = false;
        this.hasLoadedPage = false;
        this.invoicesRequestVersion++;
        this.invoices = [];
        this.invoicesWithoutPaymentMethod = [];
        this.loadError = '';
        this.isLoading = false;
        this.spinnerService.hide();
        this.dateError = '';
        this.dateQuery = {};
        const start = this.form.get('startDate').value;
        const end = this.form.get('endDate').value;
        const startDay = this.localDay(start);
        const endDay = this.localDay(end);
        if ((start && !startDay) || (end && !endDay) || (startDay && endDay && startDay > endDay)) {
            this.dateError = 'Enter a valid date range with the start date on or before the end date.';
            return;
        }
        if (startDay) {
            this.dateQuery.createdGte = Math.floor(startDay.getTime() / 1000);
        }
        if (endDay) {
            // Calendar arithmetic keeps the end inclusive even on 23/25-hour DST days.
            endDay.setDate(endDay.getDate() + 1);
            this.dateQuery.createdLt = Math.floor(endDay.getTime() / 1000);
        }
        this.loadPage(0);
    }

    private localDay(value: string): Date | null {
        if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            return null;
        }
        const [year, month, day] = value.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return year >= 1970 && date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
            ? date : null;
    }

    clearDates(): void {
        this.form.patchValue({ startDate: '', endDate: '' });
    }

    previousPage(): void {
        if (!this.isLoading && !this.loadError && this.pageIndex > 0) {
            this.loadPage(this.pageIndex - 1);
        }
    }

    nextPage(): void {
        if (!this.isLoading && !this.loadError && this.hasMore && this.nextCursor) {
            this.pageCursors[this.pageIndex + 1] = this.nextCursor;
            this.loadPage(this.pageIndex + 1);
        }
    }

    retryPage(): void {
        if (!this.isLoading && this.selectedPaymentMethod && !this.dateError) {
            this.loadPage(this.pageIndex);
        }
    }

    private loadPage(index: number): void {
        const requestVersion = ++this.invoicesRequestVersion;
        this.pageIndex = index;
        this.invoices = [];
        this.invoicesWithoutPaymentMethod = [];
        this.hasLoadedPage = false;
        this.loadError = '';
        this.isLoading = true;
        this.spinnerService.show('Loading charges...');
        const query: ChargeHistoryQuery = { ...this.dateQuery };
        if (this.pageCursors[index]) {
            query.startingAfter = this.pageCursors[index];
        }
        this.stripeService
            .getTeamChargeHistoryPage(this.selectedTeamId, this.selectedPaymentMethod, query)
            .then((page) => {
                if (requestVersion !== this.invoicesRequestVersion) {
                    return;
                }
                // An old API must fail explicitly, rather than silently displaying truncated history.
                if (!Array.isArray(page.invoices) || page.pageSize !== 50 || typeof page.hasMore !== 'boolean'
                    || (page.hasMore && !page.nextCursor)) {
                    throw new Error('Invalid Charge History page response');
                }
                const invoices = page.invoices.map((invoice) => ({
                    ...invoice, date: new Date((invoice.date as number) * 1000),
                }));
                this.invoices = invoices.filter((invoice) => invoice.withoutPaymentMethod === false);
                this.invoicesWithoutPaymentMethod = invoices.filter((invoice) => invoice.withoutPaymentMethod === true);
                this.hasMore = page.hasMore;
                this.nextCursor = page.nextCursor;
                this.pageCursors = this.pageCursors.slice(0, index + 1);
                this.hasLoadedPage = true;
                this.spinnerService.hide();
                this.isLoading = false;
            })
            .catch((err) => {
                if (requestVersion === this.invoicesRequestVersion) {
                    this.chargersLoadErrorHandler(err);
                }
            });
    }

    chargersLoadErrorHandler(err: any): void {
        this.charges = [];
        this.invoices = [];
        this.invoicesWithoutPaymentMethod = [];
        this.isLoading = false;
        this.loadError = 'Unable to load Charge History for this Team.';
        this.spinnerService.hide();
        console.error('Error in load charges ', err);
    }

    private resetPresentationState(): void {
        this.paymentMethodsRequestVersion++;
        this.invoicesRequestVersion++;
        this.selectedTeamId = '';
        this.selectedPaymentMethod = '';
        this.pageIndex = 0;
        this.pageCursors = [undefined];
        this.nextCursor = null;
        this.hasMore = false;
        this.hasLoadedPage = false;
        this.dateError = '';
        this.dateQuery = {};
        this.items = [];
        this.charges = [];
        this.invoices = [];
        this.invoicesWithoutPaymentMethod = [];
        this.isLoading = false;
        this.loadError = '';
        this.form?.setValue({ method: null, startDate: '', endDate: '' }, { emitEvent: false });
        this.spinnerService.hide();
    }

    chargeStatus(charge: any): string {
        if (charge.status === 'pending') {
            return 'Pending';
        }
        if (charge.outcome.network_status === 'declined_by_network') {
            return 'Declined';
        }
        if (charge.amount_refunded > 0 || charge.description.includes('No Charge')) {
            return charge.amount === charge.amount_refunded
                ? 'Refunded'
                : `Partially Refunded $${(charge.amount_refunded === 0
                      ? charge.amount_refunded / 100
                      : charge.amount / 100
                  ).toFixed(2)}`;
        }
        return 'Approved';
    }
}
